import { useCallback, useEffect, useState } from 'react';
import { agreementsPlatformApi } from '@/modules/agreements/services/platform-api';
import type { AgreementVersionResponse } from '@/api/schemas/agreements.types';

/** One shared empty history, so an unselected pane allocates nothing per render. */
const NO_VERSIONS: ReadonlyArray<AgreementVersionResponse> = [];

type VersionHistoryState = {
  /** The read this result belongs to; `null` until a read has settled. */
  readonly key: string | null;
  readonly versions: ReadonlyArray<AgreementVersionResponse>;
  readonly error: Error | null;
};

const EMPTY_STATE: VersionHistoryState = { key: null, versions: NO_VERSIONS, error: null };

/**
 * The version history of one document, read only when a version is SELECTED.
 *
 * WHY SELECTION IS THE TRIGGER
 * ----------------------------
 * "A read the operator did not ask for is not made." The pane exists to answer
 * "what does the selected version say?", so with no selection there is no question
 * and no request. Loading the history on mount would read every version's content —
 * drafts included — for an operator who has not asked to see any of it.
 *
 * WHY A REQUEST KEY RATHER THAN A BOOLEAN
 * ---------------------------------------
 * A boolean settles for the PREVIOUS selection, and the pane would render the
 * previous version's content (or its error) under the new selection's heading for a
 * frame. The result carries the key of the read that produced it, so "loading" means
 * exactly "this selection has no answer yet", a retry is pending the moment it is
 * asked for, and nothing is reset synchronously inside the effect.
 *
 * ONE READ PER SELECTION CHANGE: the effect depends on the selection and a retry
 * counter only, so ordinary re-renders — the page reloads the document after a
 * publish — cannot trigger a second read.
 */
export function useVersionContent(documentId: string, selectedVersionId: string | null) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<VersionHistoryState>(EMPTY_STATE);

  const key = selectedVersionId === null ? null : `${documentId}:${selectedVersionId}:${attempt}`;

  useEffect(() => {
    if (selectedVersionId === null) return;

    let cancelled = false;
    const requestKey = `${documentId}:${selectedVersionId}:${attempt}`;

    void (async () => {
      try {
        const page = await agreementsPlatformApi.listVersions(documentId);
        if (!cancelled) setState({ key: requestKey, versions: page.versions, error: null });
      } catch (cause) {
        if (cancelled) return;
        setState({
          key: requestKey,
          versions: NO_VERSIONS,
          error: cause instanceof Error ? cause : new Error(String(cause)),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [documentId, selectedVersionId, attempt]);

  const reload = useCallback(() => setAttempt((previous) => previous + 1), []);
  const settled = state.key === key;

  return {
    versions: key === null ? NO_VERSIONS : state.versions,
    error: settled ? state.error : null,
    isLoading: key !== null && !settled,
    reload,
  };
}
